// ===================================================
// git-actions — client helper for the /api/git/* routes.
// ===================================================
// The routes are behind requireAuth (Authorization: Bearer <supabase token>).
// Plain fetch() without that header returns 401 — which is why the existing Git
// buttons silently failed. Every caller MUST go through here so the token is
// always attached. One source (Rule 10).
import { supabase } from "@/lib/supabaseClient";

export interface GitStatus {
  branch: string;
  modified: string[];
  untracked: string[];
  commits: { hash: string; message: string }[];
  isDirty: boolean;
  ahead: number; // commits not yet on any remote (🟡 committed-not-pushed)
  behind: number;
  hasUpstream: boolean;
  mutationsEnabled: boolean;
}

export type GitStatusResult =
  | { ok: true; status: GitStatus }
  | { ok: false; code: number; error: string };

export interface GitCommitRequest {
  operationVersion: "gam.work-custody-local/v1";
  workspaceId: string;
  leaseId: string;
  fence: number;
  baseSha: string;
  selectedPaths: string[];
  message: string;
  idempotencyKey: string;
  preflightDigest: string;
}
export interface GitCommitResult { success: boolean; replayed?: boolean; receipt?: unknown; hash?: string; message?: string; error?: string }
export interface GitDeployResult { success: boolean; commitHash?: string; branch?: string; pushResult?: string; error?: string }

async function authHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Live git state of the serving host (vdev checkout on vdev, dev tree on localhost).
 *  Returns ok:false with code 403 when the environment disallows git actions
 *  (real production) — callers should hide the UI in that case. */
export async function gitStatus(): Promise<GitStatusResult> {
  const res = await fetch("/api/git/status", { headers: { ...(await authHeaders()) } });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    return { ok: false, code: res.status, error: body.error || res.statusText };
  }
  return { ok: true, status: (await res.json()) as GitStatus };
}

/** Commit only the explicitly selected paths. Stage 3 remains local. */
export async function gitCommit(request: GitCommitRequest | string): Promise<GitCommitResult> {
  const res = await fetch("/api/git/commit", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    // String input is a legacy compatibility path. It is intentionally
    // incomplete and remains fail-closed after F0.2 activation.
    body: JSON.stringify(typeof request === "string" ? { message: request } : request),
  });
  return res.json();
}

/** commit-if-dirty + git push origin <branch>. Advances to stage 4 (on GitHub).
 *  NOTE: named "deploy" historically but it PUSHES to GitHub — it is NOT a Vercel deploy. */
export async function gitPush(message?: string): Promise<GitDeployResult> {
  const res = await fetch("/api/git/push", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    body: JSON.stringify({ message }),
  });
  return res.json();
}

/** @deprecated Compatibility alias. This never means a Vercel deployment. */
export const gitDeploy = gitPush;
